import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  MatDialog,
  MatDialogConfig,
} from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { GenericDialogDto } from '../../data/models/dto/generic-dialog.dto';
import { SchoolDto } from '../../data/models/dto/school.dto';
import { genericDialog } from '../../data/utils/generic-dialog.utils';
import { CustomSnackbarService } from '../../ui/components/custom-snackbar/custom-snackbar-service';
import { LinkFormComponent } from './components/link-form/link-form.component';
import { LinkParamsDto } from './models/link-params.dto';
import { CreateLink, Link } from './models/link.interface';
import { LinksService } from './services/links.service';
import { UserUtil } from '../../data/utils/user.utils';

@Component({
  selector: 'app-links',
  templateUrl: './links.component.html',
  styleUrls: ['./links.component.scss'],
})
export class LinksComponent implements OnInit, OnDestroy {
  private dialogConfig: MatDialogConfig = {
    disableClose: true,
    autoFocus: true,
  };

  links: Link[] = [];
  hasChanges: boolean = false;
  previewing: boolean = false;

  private subscribeDialog = new Subscription();
  private serviceSubscription = new Subscription();

  dialogInfo: GenericDialogDto = {
    imageUrl: 'assets/images/throw_away.svg',
    primaryText: 'Você tem certeza que deseja descartar as modificações?',
    secondaryText:
      'Todas as informações não salvas serão perdidas. Sendo assim, nenhuma modificação será refletida no aplicativo.',
    btnCancelText: 'Não, cancelar',
    btnConfirmText: 'Sim, descartar',
  };

  user = this.userUtil.getUser();
  school: SchoolDto = this.user.schools[0];

  constructor(
    private readonly dialog: MatDialog,
    private readonly linksService: LinksService,
    private readonly snackBar: CustomSnackbarService,
    private readonly userUtil: UserUtil
  ) {}

  ngOnInit(): void {
    this.getLinks();
  }

  ngOnDestroy(): void {
    this.serviceSubscription.unsubscribe();
  }

  getLinks() {
    const params = { idSchool: this.school.id };
    const sub = this.linksService.getAll(params).subscribe(
      (res) => {
        this.links = res.sort((a, b) => a.order - b.order);
      },
      (error) => {
        console.log(error);
        this.snackBar.showNotification('Falha ao carregar os links', 2000);
      }
    );
    this.serviceSubscription.add(sub);
  }

  onUpdateLink(link: Link) {
    this.openUpdateLinkDialog(link);
  }

  onDeleteLink(link: Link) {
    const index = this.links
      ? this.links.findIndex((item) => item.order == link.order)
      : -1;
    if (index >= 0) {
      this.links?.splice(index, 1);
      this.links
        ?.filter(
          (item) =>
            item.order > link.order && item.order <= this.links!.length + 1
        )
        .forEach((link) => {
          link.order--;
        });
      this.hasChanges = true;
    }
  }

  onNewLink() {
    this.openNewLinkDialog();
  }

  onPreviewLink() {
    this.previewing = true;
  }

  onClosePreview() {
    this.previewing = false;
  }

  async onDiscardChanges() {
    const confirmed = await genericDialog(
      this.dialog,
      this.dialogInfo,
      'custom-dialog-confirmation'
    ).toPromise();
    if (confirmed) {
      this.hasChanges = false;
      this.getLinks();
    }
  }

  async onSave() {
    const links = this.links.map((link: Link) => {
      return {
        name: link.name,
        path: link.path,
        iconWeb: link.iconWeb,
        idsProfiles: link.idsProfiles,
        iconMobile: link.iconMobile,
        order: link.order,
        idIntegration: link.integration?.id,
        idSchool: link.idSchool,
        idsClassRooms: link.idsClassRooms,
        year: link.year
      } as CreateLink;
    });

    const params = { idSchool: this.school.id };
    if (links.length == 0) {
      this.commitLinksDeletedAll(params);
    } else {
      this.commitLinksChanges(links);
    }
  }

  private commitLinksDeletedAll(params: LinkParamsDto) {
    const subscription = this.linksService.deleteMany(params).subscribe(
      () => {
        this.hasChanges = false;
        this.snackBar.showNotification(
          'As modificações foram salvas no aplicativo',
          2000
        );
      },
      (error) => {
        console.log(error);
        this.snackBar.showNotification('Falha ao salvar os links', 2000);
      }
    );
    this.serviceSubscription.add(subscription);
  }

  private commitLinksChanges(links: CreateLink[]) {
    const subscription = this.linksService.createMany(links).subscribe(
      () => {
        this.hasChanges = false;
        this.snackBar.showNotification(
          'As modificações foram salvas no aplicativo',
          2000
        );
      },
      (error) => {
        console.log(error);
        this.snackBar.showNotification('Falha ao salvar os links', 2000);
      }
    );
    this.serviceSubscription.add(subscription);
  }

  createLink(link: Link) {
    link.order = this.links?.length! + 1;
    this.links?.push(link);
    this.hasChanges = true;
  }

  updateLink(link: Link) {
    this.links = this.links.map((l) => {
      if (l.order == link.order) {
        l = link;
        this.hasChanges = true;
      }
      return l;
    });
  }

  openNewLinkDialog(link?: Link): void {
    this.dialogConfig.data = {
      link: link,
      idSchool: this.school.id,
      isUpdate: false,
    };
    const dialogRef = this.dialog.open(LinkFormComponent, this.dialogConfig);

    this.subscribeDialog = dialogRef.componentInstance.onCreateLink.subscribe(
      (link: Link) => {
        this.createLink(link);
      }
    );

    dialogRef.afterClosed().subscribe((result) => {
      this.subscribeDialog.unsubscribe();
    });
  }

  openUpdateLinkDialog(link: Link): void {
    this.dialogConfig.data = {
      link: link,
      idSchool: this.school.id,
      isUpdate: true,
    };
    const dialogRef = this.dialog.open(LinkFormComponent, this.dialogConfig);

    this.subscribeDialog = dialogRef.componentInstance.onUpdateLink.subscribe(
      (link: Link) => {
        this.updateLink(link);
      }
    );

    dialogRef.afterClosed().subscribe((result) => {
      this.subscribeDialog.unsubscribe();
    });
  }

  drop(event: CdkDragDrop<Link[]>) {
    moveItemInArray(this.links!, event.previousIndex, event.currentIndex);

    let order = 0;
    this.links?.forEach((link) => {
      link.order = 1 + order++;
    });
    this.hasChanges = true;
  }
}
